import { IPersonaProps, ValidationState } from "@fluentui/react";
import { stringUtils } from "cayo.ui";
import { OdataQuery } from "odata";
import {
  Entity,
  PossibleValueItem,
  PropertyIcon,
  SoftLink,
} from "@cayo/shared/api/types/cayo-graph";
import { IObjectPicker } from "../../api/schema.api";
import ajax from "@cayo/shared/libs/ajax";
import logger from "@cayo/shared/libs/logger";
import { oClient } from "@cayo/shared/libs/oDataClient";
import { endpoints } from "@cayo/shared/services/endpoints.service";
import { objectUtils } from "@cayo/shared/utils/object";
import Icon from "../Graphics/Icon";
import { IPickerItem } from "./IPickerItem";
import oDataConverters from "./odata-converters";

const log = logger.getLogger("ObjectPicker.logic");

type FilterOperator = string;

function buildSearchFilter(
  field: string,
  value: string,
  operator: FilterOperator,
  additionalFilter?: string
): string {
  let base = "";

  switch (operator) {
    case "contains":
      base = `contains(${field},'${value}')`;
      break;

    case "equals":
      base = `${field} eq '${value}'`;
      break;

    case "startsWith":
      base = `startswith(${field},'${value}')`;
      break;
  }

  if (additionalFilter) {
    return `(${base}${additionalFilter})`;
  }

  return `(${base})`;
}

export function getTextFromItem(persona: IPersonaProps): string {
  return persona.text as string;
}

export function validateInput(input: string): ValidationState {
  if (input.indexOf("@") !== -1) {
    return ValidationState.valid;
  } else if (input.length > 1) {
    return ValidationState.warning;
  } else {
    return ValidationState.invalid;
  }
}

export type fetchObjectsParams = {
  path: string | undefined;
  searchText?: string | undefined;
  orderBy?: string | string[] | undefined | null;
  desc?: boolean;
  filter?: string | undefined;
  select?: string | undefined;
  expand?: string | undefined;
  additionalFilter?: string;
  body?: any;
  operator?: FilterOperator;
  controller?: AbortController;
};

export const fetchObjects: (prms: fetchObjectsParams) => Promise<Entity[]> = ({
  path,
  searchText,
  orderBy,
  desc,
  filter,
  select,
  expand,
  additionalFilter,
  body,
  operator,
  controller,
}) => {
  log.debug("fetchObjects -> args", {
    path,
    searchText,
    orderBy,
    desc,
    filter,
    select,
    expand,
    additionalFilter,
    body,
  });

  let [url, search] = path!.split("?");
  const params = new URLSearchParams(search);

  url = url.replace(/^\//, "");

  const c = oClient(endpoints.publicUrl, {
    signal: controller?.signal,
  });
  let q: Promise<any>;
  if (body) {
    const actualBody = objectUtils.deepClone(body);
    if (searchText && actualBody.parameters) {
      actualBody.parameters.push({ name: "CayoQuickSearch", value: searchText });
    }
    q = c.post(url, actualBody).query();
  } else {
    const query: OdataQuery = {
      $select: params.get("$select") ?? undefined,
      $filter: params.get("$filter") ?? undefined,
    };

    if (searchText) {
      const currentOperator: FilterOperator = operator ?? "contains";

      const anr = buildSearchFilter("objectName", searchText, currentOperator, additionalFilter);

      query.$filter = query.$filter ? `${query.$filter} and ${anr}` : anr;
    }
    if (filter) {
      if (query.$filter) {
        query.$filter = `(${query.$filter} and ${filter})`;
      } else {
        query.$filter = filter;
      }
    }

    if (select) {
      query.$select = "id,objectPath," + select;
    }
    if (!query.$select) {
      query.$select = "*";
    }
    if (!query.$filter) {
      delete query.$filter;
    }
    const actualOrderBy = Array.isArray(orderBy)
      ? orderBy.join(",")
      : `${orderBy ?? "objectName"} ${desc ? "desc" : "asc"}`;

    const queryObject = {
      ...query,
      ...(expand ? { $expand: expand } : {}),
    };

    if (orderBy !== null) {
      queryObject.$orderby = actualOrderBy;
    }

    q = c.get(url).query(queryObject);
  }

  return q
    .then((response: Entity[] | undefined) => {
      const unknownResponse = Array.isArray(response) ? (response[0] as any) : undefined;
      // case for Admin WebUI, FilterByOU. TODO: add response transformers to UI schema
      if (unknownResponse?.PVData) {
        const pvData = JSON.parse(unknownResponse.PVData)?.FilterByOU as any[];
        if (pvData?.length) {
          response = pvData.map((d) => ({
            id: d.value,
            displayName: d.name,
            name: d.name,
            value: d.value,
          }));
        }
      }

      return response || [];
    })
    .catch((e) => {
      if (!controller?.signal.aborted) {
        logger.error(e);
      }
      throw e;
    });
};

export type fetchObjectsFunc = typeof fetchObjects;

const getPropValue = (o: any, prop: string) => {
  return o[prop] ?? o[stringUtils.toCamelCase(prop)!];
};

export const EntitiesToPickerItems = (
  entities: Entity[],
  getIconAnnotation: (type?: string, dontUseDefaultIcon?: boolean) => PropertyIcon,
  picker?: IObjectPicker | undefined,
  possibleValues?: PossibleValueItem[]
) => {
  // guardian picker
  const selectPropertyName = picker?.selectPropertyName;
  const isSimplePicker = !selectPropertyName && !picker?.columns?.length;

  // admin picker
  const displayNameProp = picker?.displayNameProp;
  const displayNameAlternateProp = picker?.displayNameAlternateProp;
  const isAdminPicker = !!displayNameProp || !!displayNameAlternateProp;

  return entities
    ?.map<IPersonaProps>((v) => {
      try {
        const key = (v as any)?.key;
        if (!v!.id && !selectPropertyName && !isAdminPicker) {
          return {};
        }

        if (key && !v!.id && selectPropertyName) {
          v!.id = key;
        }

        const pickerItem = v as IPickerItem;
        const selectedPropertyValue = selectPropertyName && getPropValue(v, selectPropertyName);
        let iconParams: string | undefined;

        if (v.linkType) {
          iconParams = v.linkType;
        }

        if (v.objectType && !selectPropertyName && v.objectType !== "cayo.graph.eventCategory") {
          iconParams =
            v.objectType === "cayo.graph.objectTypeElement" ? v.objectName : v.objectType;
        }

        const displayName =
          (v as any).displayName || (v as any).DisplayName || v.objectName || selectedPropertyValue;
        const object = (v as any).object;
        const result: IPickerItem = {
          id: v.id! || (typeof object === "string" ? object : displayName),
          text:
            possibleValues?.find((pv) => pv.value === v.id!)?.name ||
            displayName ||
            key ||
            pickerItem.text,
          link: picker?.valueTemplate
            ? oDataConverters.objectToPickerValue(picker?.valueTemplate, v)
            : selectPropertyName || isSimplePicker
              ? selectedPropertyValue || key || pickerItem.link
              : oDataConverters.entityToSoftLink(v),

          onRenderPersonaCoin: () => {
            return iconParams ? (
              <Icon
                iconName={
                  getIconAnnotation ? getIconAnnotation(iconParams, true).iconId : undefined
                }
              />
            ) : null;
          },
        };

        return result;
      } catch (e) {
        logger.error(e);
        return {};
      }
    })
    .filter((i) => !!i.id && typeof i.text === "string");
};

export const StringsToEntities = (
  value: string[] | undefined,
  selectPropertyName: string | undefined,
  possibleValues?: PossibleValueItem[]
) => {
  const actualValue = Array.isArray(value) ? value : value && [value];

  return (
    actualValue?.map<IPickerItem>((v) => {
      const item: IPickerItem = {
        id: v,
        text: possibleValues?.find((p) => p.value === v)?.name || v,
        link: v,
      };

      if (selectPropertyName) {
        item[selectPropertyName] = v;
      }
      return item;
    }) || []
  );
};

export const resolveSoftLink = async (path: string, defaultValue: string) => {
  if (!path) {
    return;
  }

  const filter = defaultValue
    .split(";")
    .map((v) => `id eq '${v}'`)
    .join(" or ");
  const r = (await ajax.getClient(endpoints.publicUrl + "/" + path, "GET", undefined, undefined, {
    $filter: filter,
  })) as SoftLink[];

  const result = r.map<SoftLink>(oDataConverters.entityToSoftLink);
  return result;
};
